/**
 * @fileoverview Cliente HTTP único do painel. Centraliza o token de sessão, a leitura do
 * envelope `{ success, data, error }` e a conversão de falhas em mensagens legíveis.
 */

export const AUTH_TOKEN_KEY = "affiliate_flow_auth_token";

export class ApiRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

function authHeaders(): Record<string, string> {
  const token = typeof window === "undefined" ? null : localStorage.getItem(AUTH_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function errorMessage(body: unknown, status: number, statusText: string): string {
  if (body && typeof body === "object" && "error" in body && typeof body.error === "string") {
    return body.error;
  }
  if (typeof body === "string" && body.trim()) return body.trim().slice(0, 240);
  return `Erro HTTP ${status}${statusText ? `: ${statusText}` : ""}.`;
}

/**
 * Executa uma requisição à API e devolve o campo `data` do envelope.
 * Lança `ApiRequestError` com a mensagem retornada pela API quando a resposta não é bem-sucedida.
 */
export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const hasBody = init.body !== undefined && init.body !== null;
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...authHeaders(),
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }

  const envelope = body && typeof body === "object"
    ? body as { success?: boolean; data?: T }
    : null;
  if (!response.ok || !envelope || envelope.success === false) {
    throw new ApiRequestError(
      errorMessage(body, response.status, response.statusText),
      response.status,
    );
  }
  return envelope.data as T;
}

/** Remove o token de sessão local (usado no logout). */
export function clearSession(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}
